import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Typography, Tabs,
} from 'antd';
import moment from 'moment';
import {
  RocketOutlined, HomeOutlined, DashboardOutlined, NotificationOutlined, InfoCircleOutlined, LineChartOutlined,
} from '@ant-design/icons';
import { useParams, useHistory } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import MapQueryTypes from '../../../services/query-types/map';
import { getDatasetSummaries, getObsDataV2, getObsLatestData } from '../../../services/dataset.service';
import BuoyOutlineIcon from '../../../components/icons/BuoyOutline';
import { useData } from '../../../contexts/DataContext';
import './PlatformDashboard.scss';
import ParametersTab from './parameters/ParametersTab';
import Dashboard from './dashboard/Dashboard';
import PlatformType from './PlatformType';
import PlatformEvent from './PlatformEvent';
import ConnectionStatus from './ConnectionStatus';
import DeploymentsTab from './DeploymentsTab';
import InformationTab from './InformationTab';
import AlertsTab from './AlertsTab';
import AnalyticsTab from './AnalyticsTab';
import Breadcrumb from './Breadcrumb';

const PlatformDashboard = () => {
  const { obsDatasetId: obsDatasetIdParam, tabKey } = useParams();
  const obsDatasetId = parseInt(obsDatasetIdParam, 10);

  const history = useHistory();

  const {
    useGlobalData,
  } = useData();

  const {
    parameterConfigurationsResult,
    organizationsResult,
    registeredParametersResult,
  } = useGlobalData();

  const {
    data: obsLatestData,
    refetch: refetchObsLatest,
  } = useQuery(MapQueryTypes.REST_OBSERVATIONAL_LATEST_DATA, () => getObsLatestData(), { refetchOnWindowFocus: false });

  const { data: obsDatasetSummariesResult } = useQuery(
    MapQueryTypes.REST_OBS_DATASET_SUMMARIES_DATASET_DETAIL,
    () => getDatasetSummaries(),
    { refetchOnWindowFocus: false },
  );

  const obsDatasetSummary = obsDatasetSummariesResult?.find((o) => o.obs_dataset_id === obsDatasetId);

  const [filterValue, setFilterValue] = useState(null);
  useEffect(() => {
    if (obsDatasetSummary?.obs_dataset_id) {
      const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');
      setFilterValue({
        startDate: yesterday,
        obsDatasetId: [obsDatasetSummary.obs_dataset_id],
        includeQartod: true,
      });
    }
  }, [obsDatasetSummary]);

  const {
    data: obsDataV2,
    isLoading: obsDataV2IsLoading,
  } = useQuery(
    [MapQueryTypes.REST_OBSERVATIONAL_V2_DATA, filterValue],
    () => getObsDataV2(filterValue), {
      refetchOnWindowFocus: false,
      enabled: !!filterValue?.obsDatasetId?.length,
    },
  );

  const onObsDateRangeChange = (range) => {
    const startDate = moment().subtract(range, 'days').format('YYYY-MM-DD');
    setFilterValue({
      ...filterValue,
      startDate,
    });
  };

  const platformOrganization = organizationsResult?.find((o) => o.organization_id === obsDatasetSummary?.organization_id);

  const platformObsLatest = obsLatestData?.find((ds) => ds.obs_dataset_id === obsDatasetId);

  const latestObservations = platformObsLatest?.parameters?.map((parameterObs) => {
    const parameter = registeredParametersResult?.find((p) => p.parameter_id === parameterObs.parameter_id);
    const parameterConfig = parameterConfigurationsResult?.find((p) => p.standard_name === parameter?.standard_name);
    return {
      ...parameterObs,
      parameterConfig,
      parameter,
    };
  });

  const onTabChange = (key) => {
    history.push(`/data-console/manage/${obsDatasetId}/${key}`);

    // reset to 24 hours when tab changes
    onObsDateRangeChange(1);
  };

  // TODO: abstract this and reuse where needed to keep dry
  const timestamps = platformObsLatest?.parameters?.flatMap((p) => p.observations?.map((obs) => obs.timestamp) || []) || [];
  const latestTimestamp = timestamps.length > 0 ? Math.max(...timestamps) : null;
  const lastUpdated = latestTimestamp
    ? `updated ${moment(latestTimestamp * 1000).fromNow()}`
    : 'No recent observations';

  const isMediumScreen = useMediaQuery({ maxWidth: 940 });

  return (
    <div
      className="platform-management"
      style={{
        padding: 20,
      }}
    >
      <Breadcrumb
        activeTab={tabKey}
        obsDatasetSummary={obsDatasetSummary}
      />
      <div
        style={{
          display: isMediumScreen ? 'block' : 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 20,
            }}
          >
            <div
              style={{
                background: '#3f6c7f',
                borderRadius: '50%',
                height: 60,
                width: 60,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <BuoyOutlineIcon
                style={{
                  color: '#fff',
                  width: 25,
                  height: 25,
                }}
              />
            </div>
          </div>
          <div>
            <Typography.Title
              level={4}
              style={{
                color: '#fff',
                marginBottom: '0.2em',
              }}
            >
              {obsDatasetSummary?.platform_name}
            </Typography.Title>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px 24px',
                flexWrap: 'wrap',
              }}
            >
              <Typography.Text
                style={{
                  color: '#fff',
                  opacity: 0.8,
                  fontWeight: 'bold',
                }}
              >
                {obsDatasetSummary?.org_platform_id}
              </Typography.Text>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <PlatformType platformType={obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_type} />
              </div>
              <div
                style={{
                  color: '#fff',
                  opacity: 0.8,
                }}
              >
                {platformOrganization?.name}
                {/* TODO: link to organization page */}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            // justifyContent: 'space-between',
            alignItems: 'center',
            margin: '10px 0',
            gap: 40,
            color: '#DAE4EA',
          }}
        >

          <div>
            Deployment
            {/* TODO: abstract to tag component */}
            <PlatformEvent platformEvent={obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_event?.event} />
            <div
              style={{
                opacity: 0.8,
              }}
            >
              {obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_event?.event_time && (
                moment(obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_event?.event_time).fromNow()
              )}
            </div>
          </div>

          <div>
            Connection
            <ConnectionStatus latestTimestamp={latestTimestamp} />
            <div
              style={{
                opacity: 0.8,
              }}
            >
              {lastUpdated}
            </div>
          </div>
        </div>
      </div>
      {/* TODO: use addIcon */}
      <Tabs
        onChange={onTabChange}
        activeKey={tabKey || 'dashboard'}
      >
        <Tabs.TabPane
          tab={(
            <>
              <HomeOutlined />
              Dashboard
            </>
          )}
          key="dashboard"
        >
          <Dashboard
            latestObservations={latestObservations}
            obsDataV2={obsDataV2}
            obsDatasetSummary={obsDatasetSummary}
            onObsDateRangeChange={onObsDateRangeChange}
            refetchObsLatest={refetchObsLatest}
            obsDataV2IsLoading={obsDataV2IsLoading}
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={(
            <>
              <DashboardOutlined />
              Parameters
            </>
          )}
          key="parameters"
        >
          <ParametersTab
            obsDatasetSummary={obsDatasetSummary}
            parameterConfigurationsResult={parameterConfigurationsResult}
            registeredParametersResult={registeredParametersResult}
            obsDataV2={obsDataV2}
            onObsDateRangeChange={onObsDateRangeChange}
            obsLatestData={obsLatestData}
            isLoading={obsDataV2IsLoading}
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={(
            <>
              <NotificationOutlined />
              Alerts
            </>
          )}
          key="alerts"
        >
          <AlertsTab />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={(
            <>
              <RocketOutlined />
              Deployments
            </>
          )}
          key="deployments"
        >
          <DeploymentsTab obsDatasetSummary={obsDatasetSummary} />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={(
            <>
              <InfoCircleOutlined />
              Information
            </>
          )}
          key="information"
        >
          <InformationTab
            obsDatasetSummary={obsDatasetSummary}
            organization={platformOrganization}
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={(
            <>
              <LineChartOutlined />
              Analytics
            </>
          )}
          key="analytics"
        >
          <AnalyticsTab obsDatasetSummary={obsDatasetSummary} />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default PlatformDashboard;
